import { 
    Box, 
    Button, 
    Checkbox, 
    CircularProgress, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    FormControlLabel, 
    Stack, 
    Switch, 
    TextField,
    Typography,
    Divider,
    Paper,
    Chip,
    IconButton
} from "@mui/material";
import { Timer, SmartToy, Close, Collections, Storage } from "@mui/icons-material";
import { useState } from "react";
import StarIcon from '../assets/icons/star.png';
import { Collection, createNewCollection } from "../../../../models/collection";
import { useClassroomActions } from "../../../../hooks/useClassroomActions";

interface NewCollectionDialogProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    classroom_id: string;
    setRunningTask: React.Dispatch<React.SetStateAction<number>>;
}

export default function NewCollectionDialog({ open, setOpen, classroom_id, setRunningTask }: NewCollectionDialogProps) {

    const { createCollection } = useClassroomActions();

    const [collection, setCollection] = useState<Collection>(createNewCollection());

    const handleCollectionChange = (field: keyof Collection, value: any) => {
        setCollection((prev) => ({
            ...prev,
            [field]: value
        }));
    }

    const handleSubmit = async () => {
        setRunningTask(collection.id);
        setOpen(false);
        await createCollection(Number(classroom_id), collection);
        setRunningTask(-1);
        setCollection(createNewCollection());
    }

    return (
        <Dialog 
            open={open} 
            onClose={() => setOpen(false)} 
            fullWidth 
            maxWidth="sm"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                }
            }}
        >
            <DialogTitle 
                sx={{ 
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '20px 24px',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '3px',
                        background: 'linear-gradient(90deg, #10b981, #059669, #10b981)',
                        backgroundSize: '200% 100%',
                        animation: 'shimmer 2s infinite',
                    },
                    '@keyframes shimmer': {
                        '0%': { backgroundPosition: '-200% 0' },
                        '100%': { backgroundPosition: '200% 0' },
                    }
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Storage/>
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                            Create New Collection
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, fontSize: '0.875rem' }}>
                            Organize a group of related items
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={() => setOpen(false)}
                    sx={{
                        position: 'absolute',
                        right: 16,
                        top: 16,
                        color: 'white',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            transform: 'scale(1.1)',
                        },
                        '&:disabled': {
                            color: 'rgba(255,255,255,0.5)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent>
                <Stack spacing={2} pt={2}>
                    <TextField
                        label="Collection Title"
                        value={collection.title}
                        onChange={(e) => handleCollectionChange("title", e.target.value)}
                        fullWidth
                        sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: '#f8f9fa',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#f0f1f3',
                            },
                            '&.Mui-focused': {
                                backgroundColor: '#ffffff',
                                boxShadow: '0 0 0 3px rgba(83, 130, 107, 0.1)',
                            },
                        },
                        '& .MuiInputLabel-root.Mui-focused': {
                            color: '#53826b',
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#53826b',
                            borderWidth: 2,
                        },
                        }}
                    />

                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'flex-end' }}>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!collection.title}
                    sx={{ 
                        backgroundColor: "#10b981",
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 4,
                        py: 1,
                        minWidth: 160,
                        '&:hover': {
                            backgroundColor: "#059669"
                        },
                        '&:disabled': {
                            backgroundColor: '#e5e7eb'
                        }
                    }}
                >
                    Create Collection
                </Button>
            </DialogActions>
        </Dialog>
    );
}